import { useState } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useFavorites } from "@/favorites/FavoritesContext";
import { useUserAuth } from "@/user/UserAuthContext";
import { useLanguage, langPrefix } from "@/i18n/LanguageContext";
import { useNavigate, useLocation } from "react-router-dom";
import { trackEvent } from "@/analytics/ga";

/**
 * SaveButton — single source of truth for the "save to favorites" action.
 *
 * Used on:
 *   - Tool cards on /tools and the homepage "Featured tools" section
 *   - Article cards on /articles and the homepage "Articles" section
 *   - Article detail page (next to the title)
 *
 * Variants:
 *   - "card" (default): small 32×32 circular button intended to be
 *      placed in a card's top-right via `absolute` positioning. Has a
 *      subtle backdrop so it's readable over both light and dark
 *      surfaces. Heart fills with the brand purple when active.
 *   - "inline": 36×36 button without a backdrop, for placements where
 *      it sits next to other content (e.g. an article header).
 *
 * Behaviour:
 *   - If the user is signed-out → show a "Sign in to save" toast with
 *     a "Sign in" action that links to the auth flow with a redirect
 *     back to the page they were on.
 *   - If signed-in → optimistic toggle via FavoritesContext + toast
 *     feedback ("Saved to your favorites" / "Removed from favorites").
 *
 * All copy is bilingual (read from `useLanguage()`); the icon flips
 * between outlined and filled per the brief.
 */

const COPY = {
  ar: {
    save: "حفظ في المفضلة",
    unsave: "إزالة من المفضلة",
    addedToast: "تمت الإضافة إلى المفضلة",
    removedToast: "تمت الإزالة من المفضلة",
    signInToast: "سجّل الدخول لحفظ العناصر",
    signInAction: "تسجيل الدخول",
    networkToast: "تعذّر الحفظ. حاول مرة أخرى.",
  },
  en: {
    save: "Save to favorites",
    unsave: "Remove from favorites",
    addedToast: "Saved to your favorites",
    removedToast: "Removed from favorites",
    signInToast: "Sign in to save tools",
    signInAction: "Sign in",
    networkToast: "Could not save. Please try again.",
  },
};

export default function SaveButton({
  itemType,
  itemSlug,
  itemId = undefined,
  variant = "card",
  className = "",
  testId,
  stopPropagation = true,
}) {
  const { lang } = useLanguage();
  const { isFavorited, toggle } = useFavorites();
  const { status } = useUserAuth();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const [busy, setBusy] = useState(false);

  const t = COPY[lang] || COPY.en;
  const active = isFavorited(itemType, itemSlug);
  const label = active ? t.unsave : t.save;

  const sizeCls =
    variant === "inline"
      ? "w-9 h-9"
      : "w-8 h-8";
  const baseSurface =
    variant === "inline"
      ? "bg-transparent hover:bg-[color:var(--sas-accent-soft)]"
      : "bg-white/85 backdrop-blur-[6px] border border-[color:var(--sas-line)] hover:border-[color:var(--sas-accent)]";
  const colorCls = active
    ? "text-[color:var(--sas-accent)]"
    : "text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-accent)]";

  const onClick = async (e) => {
    if (stopPropagation) {
      // Cards typically wrap the whole tile in a clickable Link. We
      // don't want the heart-tap to trigger the link navigation.
      e.preventDefault();
      e.stopPropagation();
    }
    if (busy) return;

    if (status !== "authenticated") {
      // Build an absolute path that includes the current language prefix
      // so that after sign-in the user is sent right back to where they
      // were (e.g. /ar/tools).
      const here = `${pathname}${search}${hash}`;
      const target = `${langPrefix(lang)}/auth/login?redirect=${encodeURIComponent(here)}`;
      // Track the gated attempt so we can measure the "save → sign-in"
      // funnel and decide whether the sign-in prompt converts.
      trackEvent("save_attempt_unauthenticated", {
        item_type: itemType,
        item_slug: itemSlug,
      });
      toast.message(t.signInToast, {
        action: {
          label: t.signInAction,
          onClick: () => navigate(target),
        },
        duration: 4500,
      });
      return;
    }

    setBusy(true);
    const wasActive = active;
    const result = await toggle(itemType, itemSlug, itemId);
    setBusy(false);

    if (result.ok) {
      // Map both directions to a single GA4 event so the "save funnel"
      // report can sum both adds and removes per item. `action` =
      // "added" / "removed" lets you split in Looker / GA4 explore.
      trackEvent(itemType === "tool" ? "save_tool" : "save_article", {
        item_type: itemType,
        item_slug: itemSlug,
        action: result.favorited ? "added" : "removed",
      });
      if (result.favorited) {
        toast.success(t.addedToast, { duration: 2000 });
      } else {
        toast.success(t.removedToast, { duration: 2000 });
      }
    } else if (result.reason === "network") {
      toast.error(t.networkToast, { duration: 3000 });
    }
    // wasActive is captured for any future UI affordance (e.g. undo
    // action). Currently informational only.
    void wasActive;
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      title={label}
      disabled={busy}
      className={`
        inline-flex items-center justify-center
        rounded-full
        transition-colors
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sas-accent)] focus-visible:ring-offset-2
        ${sizeCls} ${baseSurface} ${colorCls} ${className}
      `}
      data-testid={testId || `save-button-${itemType}-${itemSlug}`}
      data-active={active ? "true" : "false"}
    >
      <Heart
        size={variant === "inline" ? 17 : 15}
        aria-hidden
        // Filled heart when active. The fill is currentColor so the
        // colour change above (text-accent) carries through.
        fill={active ? "currentColor" : "none"}
        strokeWidth={active ? 0 : 2}
      />
    </button>
  );
}
